import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth, roleHome } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import InviteAccept from "@/pages/InviteAccept";
import DashboardLayout from "@/components/DashboardLayout";

// Platform
import PlatformOverview from "@/pages/platform/Overview";
import PlatformCompanies from "@/pages/platform/Companies";
import PlatformPlans from "@/pages/platform/Plans";
import PlatformSubscriptions from "@/pages/platform/Subscriptions";
import PlatformNotifications from "@/pages/platform/Notifications";
import PlatformAudit from "@/pages/platform/Audit";
import PlatformDemoRequests from "@/pages/platform/DemoRequests";

// Company
import CompanyOverview from "@/pages/company/Overview";
import CompanySites from "@/pages/company/Sites";
import CompanyProjects from "@/pages/company/Projects";
import CompanyTowers from "@/pages/company/Towers";
import CompanyUnits from "@/pages/company/Units";
import CompanyCustomers from "@/pages/company/Customers";
import CompanyOwnerships from "@/pages/company/Ownerships";
import CompanyUpdates from "@/pages/company/Updates";
import CompanyAlerts from "@/pages/company/Alerts";
import CompanySchedules from "@/pages/company/Schedules";
import CompanyPayments from "@/pages/company/Payments";
import CompanyInvoices from "@/pages/company/Invoices";
import CompanyTickets from "@/pages/company/Tickets";
import CompanyUsers from "@/pages/company/Users";
import CompanyProfile from "@/pages/company/Profile";
import CompanyAudit from "@/pages/company/Audit";

// Customer
import CustomerOverview from "@/pages/customer/Overview";
import CustomerUnits from "@/pages/customer/Units";
import CustomerUpdates from "@/pages/customer/Updates";
import CustomerSchedules from "@/pages/customer/Schedules";
import CustomerPayments from "@/pages/customer/Payments";
import CustomerInvoices from "@/pages/customer/Invoices";
import CustomerAlerts from "@/pages/customer/Alerts";
import CustomerTickets from "@/pages/customer/Tickets";

function ProtectedRoute({ roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="overline text-stone-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={roleHome(user.role)} replace />;
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<InviteAccept />} />

          <Route element={<ProtectedRoute roles={["platform_admin"]} />}>
            <Route path="/platform" element={<PlatformOverview />} />
            <Route path="/platform/companies" element={<PlatformCompanies />} />
            <Route path="/platform/plans" element={<PlatformPlans />} />
            <Route path="/platform/subscriptions" element={<PlatformSubscriptions />} />
            <Route path="/platform/notifications" element={<PlatformNotifications />} />
            <Route path="/platform/audit" element={<PlatformAudit />} />
            <Route path="/platform/demo-requests" element={<PlatformDemoRequests />} />
          </Route>

          <Route element={<ProtectedRoute roles={["company_admin", "company_user"]} />}>
            <Route path="/company" element={<CompanyOverview />} />
            <Route path="/company/sites" element={<CompanySites />} />
            <Route path="/company/projects" element={<CompanyProjects />} />
            <Route path="/company/towers" element={<CompanyTowers />} />
            <Route path="/company/units" element={<CompanyUnits />} />
            <Route path="/company/customers" element={<CompanyCustomers />} />
            <Route path="/company/ownerships" element={<CompanyOwnerships />} />
            <Route path="/company/updates" element={<CompanyUpdates />} />
            <Route path="/company/alerts" element={<CompanyAlerts />} />
            <Route path="/company/schedules" element={<CompanySchedules />} />
            <Route path="/company/payments" element={<CompanyPayments />} />
            <Route path="/company/invoices" element={<CompanyInvoices />} />
            <Route path="/company/tickets" element={<CompanyTickets />} />
            <Route path="/company/users" element={<CompanyUsers />} />
            <Route path="/company/profile" element={<CompanyProfile />} />
            <Route path="/company/audit" element={<CompanyAudit />} />
          </Route>

          <Route element={<ProtectedRoute roles={["customer"]} />}>
            <Route path="/portal" element={<CustomerOverview />} />
            <Route path="/portal/units" element={<CustomerUnits />} />
            <Route path="/portal/updates" element={<CustomerUpdates />} />
            <Route path="/portal/schedules" element={<CustomerSchedules />} />
            <Route path="/portal/payments" element={<CustomerPayments />} />
            <Route path="/portal/invoices" element={<CustomerInvoices />} />
            <Route path="/portal/alerts" element={<CustomerAlerts />} />
            <Route path="/portal/tickets" element={<CustomerTickets />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
